import { Link, useNavigate } from '@tanstack/react-router'
import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { api } from '../../app/api'
import { ApiFailure, call } from '../../app/errors'
import { queryKeys } from '../../app/queries'
import { t } from '../../copy'
import { hubT } from './hub/copy'

/**
 * 新建一个空项目。提交 `POST /projects`，成功后进 `/projects/$projectId`。
 *
 * `/projects/new` 是 `$projectId` 的兄弟静态路由，且写在参数段前面，
 * 否则 `new` 会被当成项目 id。
 */
export function NewProjectScreen() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [error, setError] = useState('')
  const [pending, setPending] = useState(false)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    const trimmed = name.trim()
    if (trimmed === '') {
      setError(t('project.error.name_blank'))
      return
    }
    setError('')
    setPending(true)
    try {
      const created = await call('/projects', () =>
        api.POST('/projects', {
          body: {
            name: trimmed,
            ...(slug.trim() === '' ? {} : { slug: slug.trim() }),
          },
        }),
      )
      await queryClient.invalidateQueries({ queryKey: queryKeys.projects })
      await navigate({ to: '/projects/$projectId', params: { projectId: created.id } })
    } catch (cause) {
      setError(cause instanceof ApiFailure ? cause.message : t('error.generic'))
    } finally {
      setPending(false)
    }
  }

  return (
    <div className="projects-theme bg-npc-lulu-bg-0 px-npc-grid-lg pb-npc-card-lg pt-npc-card text-npc-lulu-t2 max-[600px]:px-npc-gap-lg">
      <div className="mb-npc-gap-sm text-npc-caption text-npc-lulu-t3">
        <Link to="/projects" className="text-npc-lulu-t3 hover:text-npc-lulu-t1">
          {hubT('pjhub.title')}
        </Link>
      </div>
      <h1 className="m-0 font-npc-display text-npc-h1-sm font-semibold tracking-[-0.045em] text-npc-lulu-t1">
        {hubT('pjhub.new.title')}
      </h1>
      <p className="mt-npc-gap-xs mb-npc-card text-npc-body-sm text-npc-lulu-t3">{hubT('pjhub.new.subtitle')}</p>
      <form
        onSubmit={(event) => void onSubmit(event)}
        className="pj-card flex max-w-[560px] flex-col gap-npc-grid px-[15px] py-[13px]"
      >
        <label className="flex flex-col gap-npc-gap-xs" htmlFor="project-new-name">
          <span className="text-npc-caption text-npc-lulu-t3">{hubT('pjhub.new.name')}</span>
          <input
            id="project-new-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="h-[30px] rounded-npc-tag border border-npc-lulu-line-2 bg-npc-lulu-bg-0 px-2.5 text-npc-body-sm text-npc-lulu-t1 outline-none"
            autoComplete="off"
          />
        </label>
        <label className="flex flex-col gap-npc-gap-xs" htmlFor="project-new-slug">
          <span className="text-npc-caption text-npc-lulu-t3">{hubT('pjhub.new.slug')}</span>
          <input
            id="project-new-slug"
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
            className="pj-input h-9 border border-npc-lulu-line-2 bg-npc-lulu-bg-0 px-3 font-npc-mono text-npc-body-sm font-medium text-npc-lulu-t1 outline-none"
            autoComplete="off"
          />
        </label>
        {error ? (
          <p role="alert" className="m-0 text-npc-body-sm text-npc-lulu-warn">
            {error}
          </p>
        ) : null}
        <div>
          <button type="submit" disabled={pending} className={submitClass}>
            {pending ? hubT('pjhub.new.submitting') : t('project.action.create')}
          </button>
        </div>
      </form>
    </div>
  )
}

const submitClass =
  'inline-flex h-[30px] items-center justify-center rounded-npc-tag pj-btn-primary px-3.5 text-npc-caption disabled:opacity-50'
